/**
 * 资源节点详情弹窗。
 * Tab: 基本信息 / 影响操作 / CIP清洗
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WxbAlert,
  WxbButton,
  WxbFormField,
  WxbInputNumber,
  WxbKpiCard,
  WxbTabs,
  WxbTag,
  WxbPopconfirm,
  WxbEmpty,
  WxbSelect,
  WxbDivider,
  WxbStatusBadge,
  WxbSwitch,
  WxbSpinner,
  WxbModal,
} from '../wxb-ui';
import type {
  ResourceNode,
  ResourceNodePayload,
  ResourceNodeSchedulingProfilePayload,
  ResourceNodeTemplateReference,
} from '../ProcessTemplateV2/types';
import { processTemplateV2Api } from '../../services';
import type { WxbTagColor } from './resourceNodeConstants';
import {
  NODE_CLASS_LABEL,
  NODE_CLASS_COLOR,
  BINDABLE_CLASSES,
  isRoomSchedulingResource,
} from './resourceNodeConstants';
import CipRelationManager from './CipRelationManager';
import EquipmentEditModal from './EquipmentEditModal';

interface EquipmentDetailDrawerProps {
  node: ResourceNode | null;
  open: boolean;
  nodePath: string;
  allNodes: ResourceNode[];
  childBlueprintsCount: number;
  catalogRevision: number;
  canWrite: boolean;
  startInEditMode?: boolean;
  onClose: () => void;
  onCreateChild: (parent: ResourceNode, preferredNodeClass?: ResourceNode['nodeClass']) => void;
  onDelete: (node: ResourceNode) => void;
  onToggleActive: (node: ResourceNode) => void;
  onUpdateNode: (nodeId: number, payload: Partial<ResourceNodePayload>) => Promise<void>;
  onUpdateSchedulingProfile: (
    nodeId: number,
    payload: ResourceNodeSchedulingProfilePayload,
  ) => Promise<void>;
}

const EquipmentDetailDrawer: React.FC<EquipmentDetailDrawerProps> = ({
  node,
  open,
  nodePath,
  allNodes,
  childBlueprintsCount,
  catalogRevision,
  canWrite,
  startInEditMode = false,
  onClose,
  onCreateChild,
  onDelete,
  onToggleActive,
  onUpdateNode,
  onUpdateSchedulingProfile,
}) => {
  // ALL hooks must be called unconditionally — before any early return
  const navigate = useNavigate();
  const nodeColor: WxbTagColor = node ? (NODE_CLASS_COLOR[node.nodeClass] ?? 'neutral') : 'neutral';
  const [status, setStatus] = useState<ResourceNodeSchedulingProfilePayload['status']>('ACTIVE');
  const [capacity, setCapacity] = useState(1);
  const [isShared, setIsShared] = useState(false);
  const [isSchedulable, setIsSchedulable] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [templateReferences, setTemplateReferences] = useState<ResourceNodeTemplateReference[]>([]);
  const [templateReferencesLoading, setTemplateReferencesLoading] = useState(false);
  const [templateReferencesError, setTemplateReferencesError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const isRoomResource = isRoomSchedulingResource(node);
  const nodeDisplayLabel = isRoomResource ? '排产资源' : node ? NODE_CLASS_LABEL[node.nodeClass] : '';

  useEffect(() => {
    if (open) setEditing(startInEditMode && canWrite);
  }, [canWrite, node?.id, open, startInEditMode]);

  useEffect(() => {
    setStatus((node?.boundResourceStatus as ResourceNodeSchedulingProfilePayload['status']) ?? 'ACTIVE');
    setCapacity(node?.boundResourceCapacity ?? 1);
    setIsShared(node?.boundResourceIsShared ?? false);
    setIsSchedulable(node?.boundResourceIsSchedulable ?? true);
  }, [
    node?.boundResourceCapacity,
    node?.boundResourceIsSchedulable,
    node?.boundResourceIsShared,
    node?.boundResourceStatus,
    node?.id,
  ]);

  const templateBindingCount = node?.templateBindingCount ?? 0;

  useEffect(() => {
    if (!open || !node || templateBindingCount === 0) {
      setTemplateReferences([]);
      setTemplateReferencesLoading(false);
      setTemplateReferencesError(null);
      return undefined;
    }

    let active = true;
    setTemplateReferencesLoading(true);
    setTemplateReferencesError(null);
    void processTemplateV2Api
      .listResourceNodeTemplateReferences(node.id)
      .then((references) => {
        if (active) setTemplateReferences(references);
      })
      .catch((error) => {
        if (!active) return;
        setTemplateReferences([]);
        setTemplateReferencesError(
          error?.response?.data?.error || '工序引用加载失败',
        );
      })
      .finally(() => {
        if (active) setTemplateReferencesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [node, open, templateBindingCount]);

  const schedulingReady = Boolean(
    node?.boundResourceId
      && node.boundResourceStatus === 'ACTIVE'
      && node.boundResourceIsSchedulable,
  );

  /* ── Scheduling profile section ── */
  const schedulingProfileSection = useMemo(() => {
    if (!node) return null;
    if (!BINDABLE_CLASSES.has(node.nodeClass)) {
      return (
        <div className="equip-detail-placeholder">
          <p>当前节点类型不参与排产</p>
        </div>
      );
    }

    const protectedByTemplate = templateBindingCount > 0;
    const statusOptions = protectedByTemplate
      ? [{ label: '启用', value: 'ACTIVE' }]
      : [
          { label: '启用', value: 'ACTIVE' },
          { label: '停用', value: 'INACTIVE' },
          { label: '维护中', value: 'MAINTENANCE' },
          { label: '已退役', value: 'RETIRED' },
        ];

    const handleSave = async () => {
      setSavingProfile(true);
      try {
        await onUpdateSchedulingProfile(node.id, {
          status,
          capacity,
          isShared,
          isSchedulable,
        });
      } finally {
        setSavingProfile(false);
      }
    };

    return (
      <div className="equip-scheduling-profile">
        <div className="equip-scheduling-resource">
          <div>
            <div className="equip-scheduling-resource-name">
              {node.boundResourceName ?? node.nodeName}
            </div>
            <div className="equip-scheduling-resource-code">
              {node.boundResourceCode ?? '保存后由系统自动生成'}
            </div>
          </div>
          <WxbStatusBadge
            status={schedulingReady ? 'success' : 'warning'}
            label={schedulingReady ? '可排产' : '待处理'}
            variant="dot"
            size="sm"
          />
        </div>

        {protectedByTemplate && (
          <WxbAlert title="工序模板引用保护" className="equip-alert-compact">
            已有 {templateBindingCount} 条工序引用此设备。系统会保留原资源关联，并禁止停用或暂停排产。
          </WxbAlert>
        )}

        <div className="equip-scheduling-grid">
          <WxbSelect
            label="排产状态"
            value={status}
            onChange={(value) => setStatus(value as ResourceNodeSchedulingProfilePayload['status'])}
            options={statusOptions}
            disabled={!canWrite || (protectedByTemplate && status === 'ACTIVE')}
          />
          <WxbInputNumber
            label="并行容量"
            min={1}
            precision={0}
            value={capacity}
            onChange={(value) => setCapacity(Number(value ?? 1))}
            disabled={!canWrite}
            className="equip-scheduling-capacity"
          />
          <WxbFormField label="允许排产" layout="horizontal">
            <WxbSwitch
              checked={isSchedulable}
              onChange={setIsSchedulable}
              disabled={!canWrite || (protectedByTemplate && isSchedulable)}
              size="sm"
            />
          </WxbFormField>
          <WxbFormField label="允许共享" layout="horizontal">
            <WxbSwitch
              checked={isShared}
              onChange={setIsShared}
              disabled={!canWrite}
              size="sm"
            />
          </WxbFormField>
        </div>

        <div className="equip-scheduling-actions">
          <span className="equip-scheduling-hint">设备名称和部门变更会自动同步到排产资源。</span>
          <WxbButton
            size="sm"
            onClick={() => void handleSave()}
            disabled={!canWrite || savingProfile}
          >
            {savingProfile ? '保存中…' : '保存排产设置'}
          </WxbButton>
        </div>
      </div>
    );
  }, [
    canWrite,
    capacity,
    isSchedulable,
    isShared,
    node,
    onUpdateSchedulingProfile,
    savingProfile,
    schedulingReady,
    status,
    templateBindingCount,
  ]);

  /* ── Impact operations tab ── */
  const impactOpsTab = useMemo(() => {
    if (templateReferencesLoading) {
      return <WxbSpinner tip="正在加载工序引用…" className="equip-impact-loading" />;
    }
    if (templateReferencesError) {
      return (
        <WxbAlert variant="error" title="工序引用加载失败">
          {templateReferencesError}
        </WxbAlert>
      );
    }
    if (!templateReferences.length) {
      return <WxbEmpty description="无绑定操作" />;
    }

    const templateCount = new Set(templateReferences.map((reference) => reference.templateId)).size;
    return (
      <>
        <div className="equip-impact-summary">
          <WxbStatusBadge
            status="running"
            label={`${templateReferences.length} 条工序 · ${templateCount} 个模板`}
            variant="dot"
            size="sm"
          />
        </div>
        <div className="equip-impact-list">
          {templateReferences.map((reference) => (
            <div key={reference.bindingId} className="equip-impact-item">
              <div className="equip-impact-main">
                <div className="equip-impact-title-row">
                  <span className="equip-impact-name">{reference.operationName}</span>
                  <WxbTag color={reference.bindingRole === 'PRIMARY' ? 'blue' : 'cyan'}>
                    {reference.bindingRole === 'PRIMARY' ? '主设备' : '并用设备'}
                  </WxbTag>
                </div>
                <div className="equip-impact-code">{reference.operationCode}</div>
                <div
                  className="equip-impact-context"
                  title={`${reference.templateName} / ${reference.stageName}`}
                >
                  {reference.templateCode} · {reference.stageCode} {reference.stageName}
                </div>
              </div>
              <WxbButton
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/process-templates/${reference.templateId}`)}
              >
                查看模板
              </WxbButton>
            </div>
          ))}
        </div>
      </>
    );
  }, [
    navigate,
    templateReferences,
    templateReferencesError,
    templateReferencesLoading,
  ]);

  /* ── CIP tab ── */
  const cipTab = useMemo(() => {
    if (!node) return null;
    if (node.nodeClass === 'UTILITY_STATION' && node.nodeSubtype === 'CIP') {
      return <CipRelationManager node={node} allNodes={allNodes} canWrite={canWrite} />;
    }
    return (
      <div className="equip-detail-placeholder">
        <p>仅 CIP 类型工作站可管理清洗关系</p>
      </div>
    );
  }, [allNodes, canWrite, node]);

  // Early return AFTER all hooks
  if (!node) return null;

  /* ── Tabs ── */
  const tabItems = [
    {
      key: 'info',
      label: '基本信息',
      children: (
        <>
          <EquipmentEditModal
            embedded
            readOnly={!editing}
            open
            mode="edit"
            editingNode={node}
            parentNode={null}
            allNodes={allNodes}
            initialChildClass={null}
            catalogRevision={catalogRevision}
            onCancel={() => setEditing(false)}
            onCreate={async () => undefined}
            onUpdate={onUpdateNode}
          />
          <WxbDivider />
          <div className="equip-detail-section-title">排产设置</div>
          {schedulingProfileSection}
        </>
      ),
    },
    {
      key: 'impact',
      label: `影响操作 (${templateBindingCount})`,
      children: impactOpsTab,
    },
    {
      key: 'cip',
      label: 'CIP清洗',
      children: cipTab,
    },
  ];

  return (
    <WxbModal
      open={open}
      onCancel={() => {
        setEditing(false);
        onClose();
      }}
      width={900}
      title={`${nodeDisplayLabel}详情`}
      footer={null}
      className="equip-detail-modal"
      closable
      destroyOnClose
    >
      <div className="equip-detail-header">
        <div className="equip-detail-title-row">
          <WxbTag color={nodeColor} className="equip-detail-type-tag">
            {nodeDisplayLabel}
          </WxbTag>
          <span className="equip-detail-name">{node.nodeName}</span>
          <span className={`equip-detail-status ${node.isActive ? 'is-active' : 'is-inactive'}`}>
            <span className={`equip-status-dot ${node.isActive ? 'is-active' : 'is-inactive'}`} />
            {node.isActive ? '运行中' : '已停用'}
          </span>
        </div>
        <div className="equip-detail-code">{node.nodeCode}</div>
        {nodePath && (
          <div className="equip-detail-path">{nodePath}</div>
        )}
      </div>

      {!editing && canWrite && (
        <div className="equip-detail-actions">
          <WxbButton variant="secondary" size="sm" onClick={() => setEditing(true)}>
            编辑
          </WxbButton>
          {childBlueprintsCount > 0 && (
            <WxbButton
              variant="secondary"
              size="sm"
              onClick={() => onCreateChild(
                node,
                node.nodeClass === 'ROOM' && node.nodeSubtype === 'MAIN_PROCESS' ? 'EQUIPMENT_UNIT' : undefined,
              )}
            >
              {node.nodeClass === 'ROOM' && node.nodeSubtype === 'MAIN_PROCESS' ? '新增设备' : '新增子节点'}
            </WxbButton>
          )}
          <WxbButton
            variant="secondary"
            size="sm"
            onClick={() => onToggleActive(node)}
            disabled={templateBindingCount > 0 && node.isActive}
            title={templateBindingCount > 0 && node.isActive ? '已有工序模板引用，不能停用' : undefined}
          >
            {node.isActive ? '停用' : '启用'}
          </WxbButton>
          <WxbPopconfirm
            title="确定删除此节点？"
            description={node.childCount > 0 ? '该节点有子节点，请先处理子节点。' : '此操作不可撤销。'}
            onConfirm={() => onDelete(node)}
            disabled={node.childCount > 0 || templateBindingCount > 0}
          >
            <WxbButton
              variant="ghost"
              size="sm"
              className="wxb-btn-danger-text"
              disabled={node.childCount > 0 || templateBindingCount > 0}
            >
              删除
            </WxbButton>
          </WxbPopconfirm>
        </div>
      )}

      <div className="equip-detail-kpi-grid">
        <WxbKpiCard title="子节点" value={node.childCount} />
        <WxbKpiCard title="所属部门" value={node.departmentCode || '全局'} />
        <WxbKpiCard title="影响操作" value={templateBindingCount} />
        <WxbKpiCard
          title="排产配置"
          value={schedulingReady ? '已就绪' : '待处理'}
        />
      </div>

      <WxbTabs items={tabItems} defaultActiveKey="info" />
    </WxbModal>
  );
};

export default EquipmentDetailDrawer;
